import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Sun, 
  Moon, 
  ArrowLeft, 
  CreditCard,
  Calendar,
  Receipt,
  Loader2,
  CheckCircle,
  AlertCircle,
  ChevronRight
} from 'lucide-react';

const BillingPage = () => {
  const { user, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadBillingHistory();
  }, []);

  const loadBillingHistory = async () => {
    try {
      const data = await api.getBillingHistory();
      setBillingHistory(data);
    } catch (error) {
      console.error('Failed to load billing history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await api.cancelSubscription();
      await refreshUser();
      toast.success('Subscription cancelled. Access continues until the end of your billing period.');
    } catch (error) {
      toast.error(error.message || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const formatPrice = (paise) => {
    return `₹${(paise / 100).toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const isSubscriptionActive = () => {
    if (user?.subscription_plan === 'free') return false;
    if (!user?.subscription_end) return false;
    return new Date(user.subscription_end) > new Date();
  };

  const daysUntilExpiry = () => {
    if (!user?.subscription_end) return 0;
    const diff = new Date(user.subscription_end) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const getPlanDisplayName = (planId) => {
    const names = {
      daily: 'Daily',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
      free: 'Free'
    };
    return names[planId] || planId;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50 border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/queryverse-logo2.png" alt="QueryVerse" className="h-8" />
            </Link>

            <div className="flex items-center gap-4">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-secondary transition-colors"
                data-testid="billing-theme-toggle"
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <Button 
                variant="ghost"
                onClick={() => navigate('/chat')} 
                className="rounded-full"
                data-testid="billing-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Chat
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight font-['Manrope']">
            Billing & Plans
          </h1>
          <p className="mt-2 text-muted-foreground">
            Manage your subscription and view billing history
          </p>
        </div>

        <div className="space-y-8">
          {/* Current Plan Card */}
          <Card className="rounded-2xl border-border/50">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="font-['Manrope'] flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Current Plan
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Your active subscription details
                  </CardDescription>
                </div>
                {isSubscriptionActive() && (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <p className="text-3xl font-semibold font-['Manrope'] capitalize">
                    {getPlanDisplayName(user?.subscription_plan || 'free')}
                  </p>
                  {isSubscriptionActive() ? (
                    <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {daysUntilExpiry()} days remaining • Renews {formatDate(user?.subscription_end)}
                      </span>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-muted-foreground">
                      {user?.messages_limit} messages per day
                    </p>
                  )}
                </div>
                
                <div className="flex gap-3">
                  {user?.subscription_plan === 'free' ? (
                    <Button 
                      onClick={() => navigate('/pricing')} 
                      className="rounded-full px-6"
                      data-testid="upgrade-btn"
                    >
                      Upgrade Plan
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  ) : (
                    <>
                      <Button 
                        variant="outline"
                        onClick={() => navigate('/pricing')} 
                        className="rounded-full"
                        data-testid="change-plan-btn"
                      >
                        Change Plan
                      </Button>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                            data-testid="cancel-subscription-btn"
                          >
                            Cancel
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-2xl">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-['Manrope']">Cancel subscription?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Your access will continue until the end of your current billing period ({formatDate(user?.subscription_end)}). After that, you'll be moved to the free plan with {user?.messages_limit || 20} messages per day.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-full">Keep Subscription</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={handleCancelSubscription}
                              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              disabled={cancelling}
                              data-testid="confirm-cancel-btn"
                            >
                              {cancelling ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Yes, Cancel'
                              )}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Billing History */}
          <Card className="rounded-2xl border-border/50">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Billing History
              </CardTitle>
              <CardDescription>
                Your past transactions and invoices
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : billingHistory.length === 0 ? (
                <div className="text-center py-12">
                  <Receipt className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No transactions yet</p>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    Your billing history will appear here after your first payment
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {billingHistory.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell className="font-medium">
                            {formatDate(tx.created_at)}
                          </TableCell>
                          <TableCell className="capitalize">
                            {tx.plan_name || getPlanDisplayName(tx.plan_id)}
                          </TableCell>
                          <TableCell>{formatPrice(tx.amount)}</TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={
                                tx.status === 'success' 
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800'
                                  : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800'
                              }
                            >
                              {tx.status === 'success' ? (
                                <CheckCircle className="h-3 w-3 mr-1" />
                              ) : (
                                <AlertCircle className="h-3 w-3 mr-1" />
                              )}
                              {tx.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Help Card */}
          <Card className="rounded-2xl border-border/50 bg-secondary/30">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-xl bg-background flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium font-['Manrope']">Need help with billing?</h3>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    If you have any questions about your subscription or billing, we're here to help. Contact us and we'll respond within 24 hours.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
